import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useBusinessResource from "../hooks/useBusinessResource";
import { businessResourceKey, invalidateBusinessResources } from "../utils/businessResourceFreshness";
import { choiceItemId, fetchOrderFormOptions, OrderFormOptions, useOrderChoiceScope } from "../components/orders/useOrderFormOptions";

export interface PosCatalogProduct {
  id_produit: number; item_type: "product" | "service"; product_id: number | null; service_id: number | null;
  nom_produit: string; prix_vente: number; quantite_stock: number | null;
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  product_type?: string; production_kind?: string | null; sellable_without_production?: boolean;
  image: string; id_categorie: number; code_qr?: string; duration_minutes?: number | null;
}

const toPosProducts = (data?: OrderFormOptions): PosCatalogProduct[] => (data?.products || []).map((item) => ({
  id_produit: item.id, item_type: item.item_type, product_id: item.product_id, service_id: item.service_id,
  nom_produit: item.name, prix_vente: item.prix_vente, quantite_stock: item.current_stock ?? null,
  stock_management_mode: item.stock_management_mode, product_type: item.product_type, production_kind: item.production_kind,
  sellable_without_production: item.sellable_without_production, image: item.image || "",
  id_categorie: item.category_id ?? 0, code_qr: item.barcode_value || item.code,
  duration_minutes: item.duration_minutes,
}));

function usePosChoiceResource(parameters: Record<string, unknown>, enabled: boolean) {
  const scope = useOrderChoiceScope();
  const resource = useBusinessResource<OrderFormOptions>({ ...scope, resource: "pos-form-options",
    parameters: [parameters], enabled, active: enabled, networkMode: "always",
    load: (signal) => fetchOrderFormOptions(scope, "pos", "create", parameters, signal),
  });
  return { ...resource, status: resource.data?.freshness === "cached" ? "cached" as const : resource.status,
    error: resource.error || (resource.data?.freshness === "cached"
      ? new Error("Les dernières données en cache doivent être revalidées.") : null) };
}

const appendChoices = <T extends { id: number }>(previous: T[] = [], next: T[] = []) =>
  Array.from(new Map([...previous, ...next].map((row) => [Number(row.id), row])).values());

type ScrollingChoices = OrderFormOptions & { firstPageHasMore: boolean };

function usePosScrollingChoices(parameters: Record<string, unknown>, enabled: boolean, searchReady: boolean) {
  const scope = useOrderChoiceScope();
  const client = useQueryClient();
  const keyText = JSON.stringify(businessResourceKey("pos-form-options", scope, ["scrolling-list", parameters]));
  const queryKey = useMemo(() => JSON.parse(keyText), [keyText]);
  const current = useRef({ keyText, enabled, searchReady });
  current.current = { keyText, enabled, searchReady };
  const generation = useRef(0);
  const pending = useRef<{ keyText: string; controller: AbortController } | null>(null);
  const [more, setMore] = useState<{ keyText: string; generation: number; loading: boolean; error: Error | null } | null>(null);
  const resource = useBusinessResource<ScrollingChoices>({ ...scope, resource: "pos-form-options",
    parameters: ["scrolling-list", parameters], enabled, active: enabled, networkMode: "always",
    load: async (signal) => {
      // Opening, focus, reconnection and invalidation restart at the first lot.
      // A pending append must not restore choices from before that refresh.
      if (current.current.keyText === keyText) {
        generation.current += 1;
        pending.current?.controller.abort();
      }
      const data = await fetchOrderFormOptions(scope, "pos", "create", { ...parameters, page: 1 }, signal);
      return { ...data, page: 1, firstPageHasMore: Boolean(data.hasMore) };
    },
  });
  useEffect(() => () => {
    if (pending.current?.keyText === keyText) {
      pending.current.controller.abort();
      pending.current = null;
    }
  }, [keyText, enabled]);

  const moreIsCurrent = more?.keyText === keyText && more.generation === generation.current;
  const loadMore = async (retry = false) => {
    const data = resource.data;
    if (!current.current.enabled || !current.current.searchReady || current.current.keyText !== keyText ||
      resource.loading || !data?.hasMore || pending.current || (moreIsCurrent && more?.error && !retry)) return;
    const controller = new AbortController();
    const operation = { keyText, controller };
    const requestGeneration = generation.current;
    pending.current = operation;
    setMore({ keyText, generation: requestGeneration, loading: true, error: null });
    try {
      const page = Number(data.page || 1) + 1;
      const next = await fetchOrderFormOptions(scope, "pos", "create", { ...parameters, page }, controller.signal);
      if (controller.signal.aborted || current.current.keyText !== keyText || !current.current.enabled || !current.current.searchReady ||
        generation.current !== requestGeneration) return;
      client.setQueryData<ScrollingChoices>(queryKey, (previous) => {
        if (previous !== data) return previous;
        const rows = parameters.kind === "entities"
          ? { entities: appendChoices(previous.entities, next.entities) }
          : { products: appendChoices(previous.products, next.products) };
        return { ...previous, ...next, ...rows, page,
          freshness: previous.freshness === "cached" || next.freshness === "cached" ? "cached" : "verified" };
      });
    } catch (error) {
      if (!controller.signal.aborted && current.current.keyText === keyText && generation.current === requestGeneration) {
        setMore({ keyText, generation: requestGeneration, loading: false,
          error: error instanceof Error ? error : new Error("Impossible de charger la suite des choix.") });
      }
    } finally {
      if (pending.current === operation) pending.current = null;
      setMore((previous) => previous?.keyText === keyText && previous.generation === requestGeneration
        ? { ...previous, loading: false } : previous);
    }
  };
  return {
    resource: { ...resource, status: resource.data?.freshness === "cached" ? "cached" as const : resource.status,
      error: resource.error || (resource.data?.freshness === "cached"
        ? new Error("Les dernières données en cache doivent être revalidées.") : null) },
    loadMore, loadingMore: Boolean(moreIsCurrent && more?.loading), loadMoreError: moreIsCurrent ? more?.error : null,
  };
}

// This hook only refreshes references. The cart, negotiated prices and checkout
// fields remain owned by GestionPos and are never initialised by a query result.
export default function usePosCatalogOptions({ enabled, customerId, items, categoryId, search, barcode } : {
  enabled: boolean; customerId: number | null; items: any[]; categoryId: number | null; search: string; barcode: string;
}) {
  const scope = useOrderChoiceScope();
  const client = useQueryClient();
  const contextKey = JSON.stringify([scope.userId, scope.companyId, scope.warehouseId]);
  const current = useRef({ contextKey, enabled });
  current.current = { contextKey, enabled };
  useEffect(() => {
    current.current.enabled = enabled;
    return () => { current.current.enabled = false; };
  }, [enabled]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [appliedCustomerSearch, setAppliedCustomerSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => { setAppliedCustomerSearch(customerSearch.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);
  useEffect(() => {
    const timer = setTimeout(() => { setAppliedSearch(search.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setCustomerSearch(""); setAppliedCustomerSearch(""); setAppliedSearch("");
  }, [contextKey]);
  const customerChoices = usePosScrollingChoices({ kind: "entities", limit: 50, search: appliedCustomerSearch,
    ...(customerId ? { selected_id: customerId } : {}) }, enabled,
    customerSearch.trim() === appliedCustomerSearch);
  const entities = customerChoices.resource;
  const catalogChoices = usePosScrollingChoices({ kind: "products", limit: 50,
    ...(barcode ? { barcode } : { search: appliedSearch,
      ...(!appliedSearch && categoryId != null ? { category_id: categoryId } : {}) }) }, enabled,
    Boolean(barcode) || search.trim() === appliedSearch);
  const catalog = catalogChoices.resource;
  const categories = usePosChoiceResource({ kind: "categories" }, enabled);
  const ids = Array.from(new Set(items.map(choiceItemId).filter((id) => Number.isSafeInteger(id) && id !== 0))).sort((a, b) => a - b);
  const idsText = ids.join(",");
  const selected = useBusinessResource<OrderFormOptions>({ ...scope, resource: "pos-form-options",
    parameters: ["selected-products", idsText], enabled: enabled && ids.length > 0, active: enabled && ids.length > 0,
    networkMode: "always", load: async (signal) => {
      const products: NonNullable<OrderFormOptions["products"]> = [];
      let cached = false;
      for (let offset = 0; offset < ids.length; offset += 100) {
        const data = await fetchOrderFormOptions(scope, "pos", "create",
          { kind: "products", item_ids: ids.slice(offset, offset + 100).join(",") }, signal);
        products.push(...data.products!); cached = cached || data.freshness === "cached";
      }
      return { products, freshness: cached ? "cached" : "verified" };
    },
  });
  const selectedProducts = { ...selected,
    status: selected.data?.freshness === "cached" ? "cached" as const : selected.status,
    error: selected.error || (selected.data?.freshness === "cached" ? new Error("Les articles du panier doivent être revalidés.") : null) };
  // Retain the selected label even when the party becomes disabled or is outside
  // the search page. A verified absence is a conflict, not an empty selection.
  const rememberedCustomer = useRef<{ contextKey: string; entity?: { id: number; name: string }; choices?: Array<{ id: number; name: string }> }>({ contextKey });
  if (rememberedCustomer.current.contextKey !== contextKey) rememberedCustomer.current = { contextKey };
  const availableCustomer = entities.data?.selectedEntity || entities.data?.entities?.find((entity) => Number(entity.id) === customerId)
    || rememberedCustomer.current.choices?.find((entity) => Number(entity.id) === customerId);
  if (availableCustomer && Number(availableCustomer.id) === customerId) rememberedCustomer.current.entity = availableCustomer;
  if (entities.data?.entities) rememberedCustomer.current.choices = entities.data.entities;
  const retainedCustomer = rememberedCustomer.current.entity?.id === customerId ? rememberedCustomer.current.entity : undefined;
  const customers = useMemo(() => {
    const values = [...(entities.data?.entities || [])];
    const selectedEntity = entities.data?.selectedEntity || retainedCustomer;
    if (selectedEntity && !values.some((entity) => Number(entity.id) === Number(selectedEntity.id))) values.unshift(selectedEntity);
    return values.map((entity) => ({ ID_Client: Number(entity.id), Nom_Raison_Sociale: entity.name }));
  }, [entities.data, retainedCustomer]);
  const catalogProducts = useMemo(() => toPosProducts(catalog.data), [catalog.data]);
  const products = useMemo(() => {
    const rows = new Map(catalogProducts.map((product) => [product.id_produit, product]));
    if (selected.data) idsText.split(",").map(Number).forEach((id) => rows.delete(id));
    toPosProducts(selected.data).forEach((product) => rows.set(product.id_produit, product));
    return Array.from(rows.values());
  }, [catalogProducts, selected.data, idsText]);
  const categoryChoices = useMemo(() => (categories.data?.categories || []).map((category) => ({
    id: Number(category.id), categorie: category.name,
  })), [categories.data]);
  const missingCustomer = Boolean(customerId && entities.status === "verified" && !entities.data?.selectedEntity);
  const missingProducts = selectedProducts.status === "verified"
    ? items.filter((item) => !selected.data?.products?.some((product) => product.id === choiceItemId(item))) : [];
  const assertContext = useCallback(() => {
    if (current.current.contextKey !== contextKey || !current.current.enabled) {
      throw new Error("Le contexte du point de vente a changé. Votre panier n’a pas été enregistré.");
    }
  }, [contextKey]);
  const refreshAll = async () => {
    assertContext();
    await Promise.all([entities.refresh({ force: true }), catalog.refresh({ force: true }), categories.refresh({ force: true }),
      ...(ids.length ? [selected.refresh({ force: true })] : [])]);
    assertContext();
  };
  const verifySelection = async () => {
    assertContext();
    const [parties, articles] = await Promise.all([entities.refresh({ force: true }), selected.refresh({ force: true })]);
    assertContext();
    if (parties.freshness === "cached" || articles.freshness === "cached") {
      throw new Error("Les clients et articles n’ont pas pu être revalidés en ligne. Votre panier est conservé ; réessayez.");
    }
    if (!parties.selectedEntity) throw new Error("Le client sélectionné n’est plus disponible. Votre panier est conservé ; choisissez un client disponible.");
    const absent = items.filter((item) => !articles.products?.some((product) => product.id === choiceItemId(item)));
    if (absent.length) throw new Error(`${absent.map((item) => item.nom_produit).join(", ")} : article indisponible. Le panier est conservé ; retirez ou remplacez les articles concernés.`);
    return toPosProducts(articles);
  };
  const invalidate = (mutation: string) => invalidateBusinessResources(client, scope, mutation);
  const applyOfflineSale = (soldItems: any[]) => {
    assertContext();
    const quantities = new Map<number, number>();
    soldItems.filter((item) => item.item_type !== "service").forEach((item) => {
      const id = choiceItemId(item); quantities.set(id, (quantities.get(id) || 0) + item.cartQuantity);
    });
    client.setQueriesData<OrderFormOptions>({ predicate: ({ queryKey: key }) => key[0] === "business-resource" &&
      key[1] === "pos-form-options" && key[2] === String(scope.userId) && key[3] === String(scope.companyId) &&
      key[4] === String(scope.warehouseId) }, (data) => data?.products ? { ...data, freshness: "cached",
      products: data.products.map((product) => {
        const sold = quantities.get(product.id);
        if (!sold || product.current_stock == null) return product;
        const strict = (!product.stock_management_mode || product.stock_management_mode === "standard") && !product.sellable_without_production;
        const stock = strict ? Math.max(0, product.current_stock - sold) : product.current_stock - sold;
        return { ...product, current_stock: stock, quantite_stock: stock };
      }) } : data);
  };
  return { scope, contextKey, entities, catalog, categories, selectedProducts, customers, products, catalogProducts, categoryChoices,
    customerSearch, setCustomerSearch, loadMoreCustomers: customerChoices.loadMore,
    customerLoadingMore: customerChoices.loadingMore, customerLoadMoreError: customerChoices.loadMoreError,
    loadMoreCatalog: catalogChoices.loadMore, catalogLoadingMore: catalogChoices.loadingMore,
    catalogLoadMoreError: catalogChoices.loadMoreError,
    searchReady: Boolean(barcode) || search.trim() === appliedSearch,
    missingCustomer, missingProducts, refreshAll, verifySelection, assertContext, invalidate, applyOfflineSale,
    selectionReady: ids.length === 0 || Boolean(selected.data),
  };
}
